// ── Social Panel ── Minimal-wire friend list view (W-C-003)
//
// Server-side has 13 endpoints under /api/friends* and /api/party* registered
// in server/cmd/gameserver/main.go (lines 1748-1762), but until this panel
// shipped there was zero client UI calling any of them. This panel reads
// GET /api/friends and renders a basic friend list with online status so the
// server endpoint has at least one live caller. Friend-request creation /
// party management is intentionally deferred to a future pass.
//
// Modelled on `leaderboard-panel.ts` for the dark-military DOM theme and the
// overlay/dispose() lifecycle, and on `skin-market.ts:23-28` (commit 971900d)
// for the authHeaders() JWT injection pattern.

import { getToken } from '../auth/token-store';

const ACCENT = '#e94560';
const BG_DARK = 'rgba(0,0,0,0.92)';
const FONT = '"Courier New", monospace';

// W-C-003: every /api/friends* endpoint is gated by authOrGuest on the
// server (server/cmd/gameserver/main.go:1748-1762). Mirror the
// skin-market.ts/ClanPanel pattern so guest tokens and signed JWTs both
// work.
function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

/** Convert a WS server URL to the HTTP `/api/friends` endpoint. */
export function buildFriendsUrl(serverUrl: string): string {
  return buildHttpBase(serverUrl) + '/api/friends';
}

/** Convert a WS server URL to a sibling HTTP base (no trailing path). */
function buildHttpBase(serverUrl: string): string {
  return serverUrl
    .replace('ws://', 'http://')
    .replace('wss://', 'https://')
    .replace('/ws', '');
}

type FriendPresenceStatus = 'online' | 'in-match' | 'offline';

interface FriendEntry {
  friend_id: string;
  status?: string;
  online_status?: string;
  presence_status?: FriendPresenceStatus;
  created_at: string;
}

interface FriendsResponse {
  friends?: FriendEntry[];
}

export interface PartyMember {
  account_id: string;
  ready?: boolean;
}

export interface PartyState {
  party_id: string | null;
  leader_id: string | null;
  members: PartyMember[];
}

type Mode = 'friends' | 'party';

export class SocialPanel {
  private overlay: HTMLElement;
  private listBody: HTMLElement;
  private loadingEl: HTMLElement;
  private partyBody: HTMLElement;
  private partyLoadingEl: HTMLElement;
  private tabFriendsBtn: HTMLElement;
  private tabPartyBtn: HTMLElement;
  private headerTitleEl: HTMLElement;
  private headerSubtitleEl: HTMLElement;
  private colHeaders: HTMLElement;
  private visible = false;
  private mode: Mode = 'friends';
  private serverUrl: string;
  private boundKeyHandler = (e: KeyboardEvent) => this.handleKeyDown(e);
  private abortController: AbortController | null = null;

  constructor(serverUrl: string) {
    this.serverUrl = serverUrl;
    const built = this.createOverlay();
    this.overlay = built.overlay;
    this.listBody = built.listBody;
    this.loadingEl = built.loadingEl;
    this.partyBody = built.partyBody;
    this.partyLoadingEl = built.partyLoadingEl;
    this.tabFriendsBtn = built.tabFriendsBtn;
    this.tabPartyBtn = built.tabPartyBtn;
    this.headerTitleEl = built.headerTitleEl;
    this.headerSubtitleEl = built.headerSubtitleEl;
    this.colHeaders = built.colHeaders;
    this.applyModeChrome();
    document.body.appendChild(this.overlay);
    document.addEventListener('keydown', this.boundKeyHandler);
  }

  async show(): Promise<void> {
    if (this.visible) {
      // Already visible — still re-fetch so the active tab refreshes.
      await this.refreshActiveTab();
      return;
    }
    this.visible = true;
    this.overlay.style.display = 'flex';
    await this.refreshActiveTab();
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.overlay.style.display = 'none';
  }

  toggle(): void {
    if (this.visible) this.hide();
    else void this.show();
  }

  isOpen(): boolean {
    return this.visible;
  }

  dispose(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    document.removeEventListener('keydown', this.boundKeyHandler);
    this.overlay.remove();
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Escape' && this.visible) {
      e.preventDefault();
      e.stopPropagation();
      this.hide();
    }
  }

  /** Switch the active tab and refresh its data. */
  setMode(mode: Mode): void {
    if (this.mode === mode) return;
    this.mode = mode;
    this.applyModeChrome();
    void this.refreshActiveTab();
  }

  private applyModeChrome(): void {
    const friendsActive = this.mode === 'friends';
    // Tab styling
    this.tabFriendsBtn.style.color = friendsActive ? ACCENT : '#888';
    this.tabFriendsBtn.style.borderBottom = friendsActive ? `2px solid ${ACCENT}` : '2px solid transparent';
    this.tabPartyBtn.style.color = !friendsActive ? ACCENT : '#888';
    this.tabPartyBtn.style.borderBottom = !friendsActive ? `2px solid ${ACCENT}` : '2px solid transparent';

    // Header copy
    this.headerTitleEl.textContent = friendsActive ? 'FRIENDS' : 'PARTY';
    this.headerSubtitleEl.textContent = friendsActive ? 'YOUR FRIEND LIST' : 'CURRENT PARTY';

    // Body visibility — friends list (with its column headers) vs. party view.
    this.colHeaders.style.display = friendsActive ? 'grid' : 'none';
    this.listBody.style.display = friendsActive ? 'block' : 'none';
    this.partyBody.style.display = friendsActive ? 'none' : 'block';
  }

  private async refreshActiveTab(): Promise<void> {
    if (this.mode === 'friends') {
      await this.fetchAndRender();
    } else {
      await this.fetchAndRenderParty();
    }
  }

  /**
   * Placeholder party fetch. Returns an empty PartyState for now — real
   * wiring will call GET /api/party (authOrGuest, see
   * server/cmd/gameserver/main.go:1835) and decode the response shape.
   */
  async fetchParty(): Promise<PartyState> {
    // TODO(BUG-P-25 follow-up): wire actual fetch against
    //   `${buildHttpBase(this.serverUrl)}/api/party`
    // once the response shape is agreed with server/internal/api/social.go.
    return { party_id: null, leader_id: null, members: [] };
  }

  private async fetchAndRenderParty(): Promise<void> {
    this.partyLoadingEl.style.display = 'block';
    try {
      const state = await this.fetchParty();
      this.renderParty(state);
    } catch (err) {
      console.error('[SocialPanel] fetchParty failed:', err);
      this.renderParty({ party_id: null, leader_id: null, members: [] });
    } finally {
      this.partyLoadingEl.style.display = 'none';
    }
  }

  private renderParty(state: PartyState): void {
    this.partyBody.innerHTML = '';

    // ── Members section ──
    const membersHeader = document.createElement('div');
    Object.assign(membersHeader.style, {
      fontSize: '10px',
      letterSpacing: '2px',
      textTransform: 'uppercase',
      color: '#666',
      padding: '10px 16px 6px',
    });
    membersHeader.textContent = `MEMBERS (${state.members.length})`;
    this.partyBody.appendChild(membersHeader);

    if (state.members.length === 0) {
      const empty = document.createElement('div');
      Object.assign(empty.style, {
        color: '#666',
        padding: '20px',
        textAlign: 'center',
        fontSize: '13px',
        letterSpacing: '1px',
      });
      empty.textContent = state.party_id ? 'Party is empty.' : 'Not in a party.';
      this.partyBody.appendChild(empty);
    } else {
      for (const m of state.members) {
        const row = document.createElement('div');
        Object.assign(row.style, {
          display: 'grid',
          gridTemplateColumns: '1fr 80px',
          padding: '10px 16px',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          alignItems: 'center',
          fontSize: '13px',
        });
        const nameEl = document.createElement('div');
        nameEl.style.color = '#fff';
        nameEl.style.fontWeight = 'bold';
        nameEl.style.overflow = 'hidden';
        nameEl.style.textOverflow = 'ellipsis';
        nameEl.style.whiteSpace = 'nowrap';
        const isLeader = state.leader_id !== null && m.account_id === state.leader_id;
        nameEl.textContent = isLeader ? `${m.account_id} (leader)` : m.account_id;
        row.appendChild(nameEl);

        const readyEl = document.createElement('div');
        readyEl.style.textAlign = 'center';
        readyEl.style.fontSize = '11px';
        readyEl.style.letterSpacing = '1px';
        readyEl.style.textTransform = 'uppercase';
        readyEl.style.color = m.ready ? '#4caf50' : '#777';
        readyEl.textContent = m.ready ? 'ready' : 'not ready';
        row.appendChild(readyEl);

        this.partyBody.appendChild(row);
      }
    }

    // ── Invite-by-name row ──
    const inviteRow = document.createElement('div');
    Object.assign(inviteRow.style, {
      display: 'flex',
      gap: '8px',
      padding: '16px',
      borderTop: '1px solid rgba(255,255,255,0.05)',
    });

    const inviteInput = document.createElement('input');
    inviteInput.setAttribute('type', 'text');
    inviteInput.setAttribute('placeholder', 'Invite by name');
    Object.assign(inviteInput.style, {
      flex: '1',
      padding: '8px 10px',
      background: 'rgba(0,0,0,0.4)',
      border: '1px solid rgba(255,255,255,0.1)',
      color: '#fff',
      fontFamily: FONT,
      fontSize: '13px',
      outline: 'none',
    });
    inviteRow.appendChild(inviteInput);

    const inviteBtn = document.createElement('button');
    inviteBtn.textContent = 'INVITE';
    Object.assign(inviteBtn.style, {
      padding: '8px 14px',
      background: 'transparent',
      border: `1px solid ${ACCENT}80`,
      color: ACCENT,
      fontFamily: FONT,
      fontSize: '11px',
      letterSpacing: '2px',
      cursor: 'pointer',
    });
    inviteBtn.addEventListener('click', () => {
      const name = (inviteInput as HTMLInputElement).value.trim();
      if (!name) return;
      // TODO(BUG-P-25 follow-up): POST to
      //   `${buildHttpBase(this.serverUrl)}/api/party/invite`
      // with { invitee_id: name } (or whatever shape social.go expects).
      // If we're not yet in a party, the server flow is to first call
      //   POST `${buildHttpBase(this.serverUrl)}/api/party/create`
      // and then issue the invite against the returned party_id.
      console.info('[SocialPanel] invite stub — would POST /api/party/invite for', name);
      (inviteInput as HTMLInputElement).value = '';
    });
    inviteRow.appendChild(inviteBtn);
    this.partyBody.appendChild(inviteRow);

    // ── Leave-party button ──
    const leaveRow = document.createElement('div');
    Object.assign(leaveRow.style, {
      padding: '0 16px 16px',
    });
    const leaveBtn = document.createElement('button');
    leaveBtn.textContent = 'LEAVE PARTY';
    Object.assign(leaveBtn.style, {
      width: '100%',
      padding: '10px',
      background: 'transparent',
      border: '1px solid rgba(255,255,255,0.15)',
      color: '#aaa',
      fontFamily: FONT,
      fontSize: '11px',
      letterSpacing: '2px',
      cursor: 'pointer',
    });
    leaveBtn.addEventListener('click', () => {
      // TODO(BUG-P-25 follow-up): POST to
      //   `${buildHttpBase(this.serverUrl)}/api/party/leave`
      // then re-fetch via fetchParty() to refresh the view.
      console.info('[SocialPanel] leave stub — would POST /api/party/leave');
    });
    leaveRow.appendChild(leaveBtn);
    this.partyBody.appendChild(leaveRow);
  }

  private async fetchAndRender(): Promise<void> {
    this.loadingEl.style.display = 'block';
    this.listBody.innerHTML = '';

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    const url = buildFriendsUrl(this.serverUrl);
    try {
      const res = await fetch(url, {
        method: 'GET',
        headers: authHeaders(),
        signal: this.abortController.signal,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as FriendsResponse;
      const friends = Array.isArray(data.friends) ? data.friends : [];
      this.renderFriends(friends);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      console.error('[SocialPanel] Fetch failed:', err);
      this.listBody.innerHTML =
        '<div style="color:#666;padding:20px;text-align:center;">Failed to load friends</div>';
    } finally {
      this.loadingEl.style.display = 'none';
    }
  }

  private renderFriends(friends: FriendEntry[]): void {
    this.listBody.innerHTML = '';

    if (friends.length === 0) {
      const empty = document.createElement('div');
      Object.assign(empty.style, {
        color: '#666',
        padding: '40px 20px',
        textAlign: 'center',
        fontSize: '13px',
        letterSpacing: '1px',
      });
      empty.textContent = 'No friends yet — invites coming soon.';
      this.listBody.appendChild(empty);
      return;
    }

    for (const f of friends) {
      const row = document.createElement('div');
      Object.assign(row.style, {
        display: 'grid',
        gridTemplateColumns: '1fr 90px 80px',
        padding: '10px 16px',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        alignItems: 'center',
        fontSize: '13px',
        transition: 'background 0.1s',
      });
      row.addEventListener('mouseenter', () => {
        row.style.background = 'rgba(255,255,255,0.03)';
      });
      row.addEventListener('mouseleave', () => {
        row.style.background = 'transparent';
      });

      // Friend ID / display name. We don't have a name lookup yet, so render
      // the raw account ID — same approach scoreboard takes for unresolved
      // identifiers.
      const presence = this.getFriendPresenceStatus(f);
      const nameWrap = document.createElement('div');
      Object.assign(nameWrap.style, {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        minWidth: '0',
      });
      const statusDot = document.createElement('span');
      Object.assign(statusDot.style, {
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        flexShrink: '0',
        background: this.getPresenceColor(presence),
        boxShadow: `0 0 8px ${this.getPresenceColor(presence)}60`,
      });
      nameWrap.appendChild(statusDot);

      const nameEl = document.createElement('div');
      nameEl.style.color = '#fff';
      nameEl.style.fontWeight = 'bold';
      nameEl.style.overflow = 'hidden';
      nameEl.style.textOverflow = 'ellipsis';
      nameEl.style.whiteSpace = 'nowrap';
      nameEl.textContent = f.friend_id;
      nameWrap.appendChild(nameEl);
      row.appendChild(nameWrap);

      // Online status (online / offline / in_match …)
      const onlineEl = document.createElement('div');
      onlineEl.style.textAlign = 'center';
      onlineEl.style.fontSize = '11px';
      onlineEl.style.letterSpacing = '1px';
      onlineEl.style.textTransform = 'uppercase';
      onlineEl.style.color = this.getPresenceColor(presence);
      onlineEl.textContent = presence;
      row.appendChild(onlineEl);

      // Friend-graph status (accepted / pending / blocked)
      const statusEl = document.createElement('div');
      statusEl.style.textAlign = 'center';
      statusEl.style.fontSize = '11px';
      statusEl.style.letterSpacing = '1px';
      statusEl.style.textTransform = 'uppercase';
      const friendshipStatus = this.isPresenceStatus(f.status) ? '' : f.status ?? '';
      statusEl.style.color = friendshipStatus.toLowerCase() === 'pending' ? '#e8b849' : '#aaa';
      statusEl.textContent = friendshipStatus;
      row.appendChild(statusEl);

      this.listBody.appendChild(row);
    }
  }

  private getFriendPresenceStatus(friend: FriendEntry): FriendPresenceStatus {
    const candidates = [friend.status, friend.online_status, friend.presence_status];
    for (const candidate of candidates) {
      if (!candidate) continue;
      const normalized = candidate.toLowerCase().replace('_', '-') as FriendPresenceStatus;
      if (this.isPresenceStatus(normalized)) return normalized;
    }
    return 'offline';
  }

  private isPresenceStatus(value: string | undefined): value is FriendPresenceStatus {
    return value === 'online' || value === 'in-match' || value === 'offline';
  }

  private getPresenceColor(status: FriendPresenceStatus): string {
    if (status === 'online') return '#4caf50';
    if (status === 'in-match') return '#e8b849';
    return '#777';
  }

  private createOverlay(): {
    overlay: HTMLElement;
    listBody: HTMLElement;
    loadingEl: HTMLElement;
    partyBody: HTMLElement;
    partyLoadingEl: HTMLElement;
    tabFriendsBtn: HTMLElement;
    tabPartyBtn: HTMLElement;
    headerTitleEl: HTMLElement;
    headerSubtitleEl: HTMLElement;
    colHeaders: HTMLElement;
  } {
    const overlay = document.createElement('div');
    overlay.setAttribute('data-panel-id', 'social');
    Object.assign(overlay.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100vw',
      height: '100vh',
      background: BG_DARK,
      zIndex: '57',
      display: 'none',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: FONT,
      color: '#ffffff',
      userSelect: 'none',
    });

    const card = document.createElement('div');
    Object.assign(card.style, {
      width: 'min(560px, calc(100vw - 24px))',
      maxHeight: '80vh',
      border: `2px solid ${ACCENT}40`,
      borderRadius: '8px',
      background: 'rgba(26,26,46,0.98)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    });

    // Header
    const header = document.createElement('div');
    Object.assign(header.style, {
      padding: '24px 24px 16px',
      borderBottom: `1px solid ${ACCENT}30`,
      textAlign: 'center',
    });

    const title = document.createElement('div');
    Object.assign(title.style, {
      fontSize: '24px',
      fontWeight: 'bold',
      letterSpacing: '4px',
      textTransform: 'uppercase',
      color: ACCENT,
      marginBottom: '4px',
    });
    title.textContent = 'FRIENDS';
    header.appendChild(title);
    const headerTitleEl = title;

    const subtitle = document.createElement('div');
    Object.assign(subtitle.style, {
      fontSize: '11px',
      color: '#555',
      letterSpacing: '2px',
    });
    subtitle.textContent = 'YOUR FRIEND LIST';
    header.appendChild(subtitle);
    const headerSubtitleEl = subtitle;
    card.appendChild(header);

    // Tab strip — Friends / Party
    const tabStrip = document.createElement('div');
    Object.assign(tabStrip.style, {
      display: 'flex',
      borderBottom: `1px solid ${ACCENT}30`,
    });
    const makeTab = (label: string, mode: Mode): HTMLElement => {
      const tab = document.createElement('div');
      tab.setAttribute('data-tab', mode);
      Object.assign(tab.style, {
        flex: '1',
        textAlign: 'center',
        padding: '10px 0',
        fontSize: '12px',
        letterSpacing: '3px',
        textTransform: 'uppercase',
        cursor: 'pointer',
        color: '#888',
        borderBottom: '2px solid transparent',
      });
      tab.textContent = label;
      tab.addEventListener('click', () => this.setMode(mode));
      return tab;
    };
    const tabFriendsBtn = makeTab('Friends', 'friends');
    const tabPartyBtn = makeTab('Party', 'party');
    tabStrip.appendChild(tabFriendsBtn);
    tabStrip.appendChild(tabPartyBtn);
    card.appendChild(tabStrip);

    // Column headers
    const colHeaders = document.createElement('div');
    Object.assign(colHeaders.style, {
      display: 'grid',
      gridTemplateColumns: '1fr 90px 80px',
      padding: '10px 16px',
      borderBottom: '1px solid rgba(255,255,255,0.1)',
      fontSize: '10px',
      color: '#666',
      letterSpacing: '1px',
      textTransform: 'uppercase',
    });
    const cName = document.createElement('div');
    cName.textContent = 'PLAYER';
    colHeaders.appendChild(cName);
    const cOnline = document.createElement('div');
    cOnline.style.textAlign = 'center';
    cOnline.textContent = 'ONLINE';
    colHeaders.appendChild(cOnline);
    const cStatus = document.createElement('div');
    cStatus.style.textAlign = 'center';
    cStatus.textContent = 'STATUS';
    colHeaders.appendChild(cStatus);
    card.appendChild(colHeaders);

    // Scrollable list
    const listBody = document.createElement('div');
    Object.assign(listBody.style, {
      flex: '1',
      overflowY: 'auto',
      minHeight: '200px',
    });
    card.appendChild(listBody);

    // Loading indicator (overlayed inside list area)
    const loadingEl = document.createElement('div');
    Object.assign(loadingEl.style, {
      textAlign: 'center',
      padding: '40px',
      color: '#666',
      fontSize: '14px',
      letterSpacing: '2px',
      display: 'none',
    });
    loadingEl.textContent = 'LOADING...';
    listBody.appendChild(loadingEl);

    // Party body — hidden by default; sibling of listBody. Built once
    // and re-rendered inside renderParty(). Starts display:none and is
    // toggled by applyModeChrome() when the user clicks the Party tab.
    const partyBody = document.createElement('div');
    Object.assign(partyBody.style, {
      flex: '1',
      overflowY: 'auto',
      minHeight: '200px',
      display: 'none',
    });
    card.appendChild(partyBody);

    const partyLoadingEl = document.createElement('div');
    Object.assign(partyLoadingEl.style, {
      textAlign: 'center',
      padding: '40px',
      color: '#666',
      fontSize: '14px',
      letterSpacing: '2px',
      display: 'none',
    });
    partyLoadingEl.textContent = 'LOADING...';
    partyBody.appendChild(partyLoadingEl);

    // Footer
    const footer = document.createElement('div');
    Object.assign(footer.style, {
      padding: '12px',
      textAlign: 'center',
      fontSize: '11px',
      color: '#444',
      borderTop: '1px solid rgba(255,255,255,0.05)',
    });
    footer.textContent = 'Friend requests UI coming in a future update.';
    card.appendChild(footer);

    overlay.appendChild(card);

    // Click outside to close
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) this.hide();
    });

    return {
      overlay,
      listBody,
      loadingEl,
      partyBody,
      partyLoadingEl,
      tabFriendsBtn,
      tabPartyBtn,
      headerTitleEl,
      headerSubtitleEl,
      colHeaders,
    };
  }
}
